import { useEffect, useState } from "react";
import { fetchBasket } from "@/lib/api";
import { SimTag } from "@/components/shared";

const BASKET_ORDER = ["SOL", "JUP", "BONK", "WIF", "RAY"];

// Orbit layout: angle (deg) + radius, tuned to frame the basket core
const ORBIT = {
  SOL: { x: 50, y: 4, delay: "0s", big: true },
  JUP: { x: 8, y: 32, delay: "0.8s", big: false },
  BONK: { x: 92, y: 32, delay: "1.6s", big: false },
  WIF: { x: 16, y: 76, delay: "2.4s", big: false },
  RAY: { x: 84, y: 76, delay: "3.2s", big: false },
};

const TokenChip = ({ asset, pos }) => (
  <div
    className="absolute token-float z-20"
    style={{ left: `${pos.x}%`, top: `${pos.y}%`, animationDelay: pos.delay, transform: "translate(-50%, -50%)" }}
    data-testid={`basket-token-${asset.symbol.toLowerCase()}`}
  >
    <div className="token-float-inner flex flex-col items-center gap-2">
      <div
        className={`${pos.big ? "w-16 h-16 md:w-20 md:h-20 text-sm" : "w-12 h-12 md:w-14 md:h-14 text-[10px]"} rounded-full flex items-center justify-center font-bold mono-font token-chip`}
        style={{
          background: `radial-gradient(circle at 32% 30%, ${asset.color}44, rgba(10,6,20,0.9))`,
          border: `1.5px solid ${asset.color}`,
          boxShadow: `0 0 18px ${asset.color}55, inset 0 0 12px ${asset.color}22`,
          color: asset.color,
        }}
      >
        {asset.symbol}
      </div>
      <span
        className="mono-font text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full bg-black/70 border"
        style={{ borderColor: `${asset.color}66`, color: asset.color }}
      >
        {asset.allocation}%
      </span>
    </div>
  </div>
);

export default function BasketVisual() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchBasket().then(setData).catch(() => {});
  }, []);

  const assets = BASKET_ORDER
    .map((sym) => (data?.assets ?? []).find((a) => a.symbol === sym))
    .filter(Boolean);
  const total = data?.treasury_value_usd ?? 248950;

  return (
    <section className="py-24 px-6 bg-[#07050d] border-b border-white/5 relative overflow-hidden" data-testid="basket-visual-section">
      <div className="absolute inset-0 sol-glow pointer-events-none opacity-60" />
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <div className="flex items-center gap-3 justify-center">
            <span className="mono-font text-xs uppercase tracking-widest text-[#14F195] font-semibold">Inside The Vault</span>
            <SimTag>Example Composition</SimTag>
          </div>
          <h2 className="display-font uppercase text-3xl md:text-5xl text-white mt-2">The Basket, Visualized</h2>
          <p className="text-sm text-[#a8a1c2] mt-3">Five curated Solana assets. One shared vault. Target weights kept in balance by automated routing.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Visual */}
          <div className="lg:col-span-7">
            <div className="relative mx-auto w-full max-w-[560px] aspect-square">
              {/* orbit rings */}
              <div className="absolute inset-[6%] rounded-full border border-[#9945FF]/15 orbit-ring" />
              <div className="absolute inset-[16%] rounded-full border border-[#14F195]/10 orbit-ring-rev" />
              {/* core glow */}
              <div className="absolute inset-[26%] rounded-full basket-core-glow" />

              {/* basket core */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center" data-testid="basket-core">
                <svg className="w-28 h-28 md:w-36 md:h-36" viewBox="0 0 120 120" fill="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="baskGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#9945FF" />
                      <stop offset="55%" stopColor="#00F0FF" />
                      <stop offset="100%" stopColor="#14F195" />
                    </linearGradient>
                  </defs>
                  <path d="M28 46 L92 46 L84 96 L36 96 Z" stroke="url(#baskGrad)" strokeWidth="3.5" strokeLinejoin="round" fill="rgba(153,69,255,0.08)" />
                  <path d="M40 46 C40 26 80 26 80 46" stroke="url(#baskGrad)" strokeWidth="3.5" strokeLinecap="round" />
                  <path d="M44 58 L76 58 M42 72 L78 72 M40 86 L80 86" stroke="rgba(230,226,248,0.25)" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="60" cy="34" r="3" fill="#14F195" />
                </svg>
                <div className="text-center mt-2">
                  <div className="mono-font text-[10px] uppercase tracking-widest text-[#9b93b8]">Total Basket Value</div>
                  <div className="mono-font text-2xl md:text-3xl font-bold text-white" data-testid="basket-total-value">
                    ${total.toLocaleString()}
                  </div>
                  <div className="sim-tag mt-2">Simulated</div>
                </div>
              </div>

              {/* floating token chips */}
              {assets.map((a) => (
                <TokenChip key={a.symbol} asset={a} pos={ORBIT[a.symbol]} />
              ))}

              {/* connecting lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                {assets.map((a) => (
                  <g key={a.symbol}>
                    {/* soft glow underlay */}
                    <line x1={ORBIT[a.symbol].x} y1={ORBIT[a.symbol].y} x2="50" y2="50"
                      stroke={a.color} strokeOpacity="0.35" strokeWidth="1.1" className="basket-line-glow" />
                    {/* animated dashed core */}
                    <line x1={ORBIT[a.symbol].x} y1={ORBIT[a.symbol].y} x2="50" y2="50"
                      stroke={a.color} strokeOpacity="0.5" strokeWidth="0.35" strokeDasharray="1.4 1.2" className="basket-line" />
                    {/* traveling pulse */}
                    <circle r="1.1" fill={a.color}>
                      <animateMotion dur="3s" repeatCount="indefinite"
                        path={`M ${ORBIT[a.symbol].x} ${ORBIT[a.symbol].y} L 50 50`} />
                    </circle>
                  </g>
                ))}
              </svg>
            </div>
          </div>

          {/* Legend */}
          <div className="lg:col-span-5 space-y-3" data-testid="basket-legend">
            {assets.map((a, i) => (
              <div key={a.symbol} className="glass-panel rounded-2xl p-4 flex items-center justify-between hover:border-[#14F195]/40 transition-colors d1-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs mono-font"
                    style={{ background: `${a.color}22`, border: `1px solid ${a.color}88`, color: a.color, boxShadow: `0 0 12px ${a.color}33` }}>
                    {a.symbol}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{a.name}</div>
                    <div className="text-[11px] text-[#9b93b8] mono-font">${a.price_demo} (Demo)</div>
                  </div>
                </div>
                <div className="w-28">
                  <div className="flex justify-between text-[11px] mono-font mb-1">
                    <span style={{ color: a.color }}>{a.allocation}%</span>
                    <span className="text-[#7a7493]">target</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full alloc-bar" style={{ width: `${a.allocation}%`, background: `linear-gradient(90deg, ${a.color}88, ${a.color})`, boxShadow: `0 0 8px ${a.color}66` }} />
                  </div>
                </div>
              </div>
            ))}
            <p className="text-[11px] text-[#7a7493] mono-font pt-2">
              Example composition and pricing for the prototype. Final basket weights will be published before launch.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
